<template>
  <div>
    <b-modal
        v-model="showModalInternal"
        id="reports-form-modal"
        scrollable
        :title="title"
        title-class="font-18"
        size="xl"
        hide-footer
    >

      <!-- Step 1: Report type selection (create mode only) -->
      <div v-if="!isEditMode && !typeSelected">
        <div class="card shadow-sm mb-4">
          <div class="card-header bg-light">
            <h6 class="mb-0 text-primary fw-bold">
              <i class="fas fa-list me-2"></i>Wybierz typ raportu
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label required">Typ raportu</label>
                <select
                    class="form-select"
                    v-model="selectedReportType"
                    :class="{'is-invalid': typeValidationError}"
                >
                  <option value="">-- Wybierz typ raportu --</option>
                  <option
                      v-for="reportType in availableReportTypes"
                      :key="reportType.type"
                      :value="reportType.type"
                  >
                    {{ reportType.displayedName }}
                  </option>
                </select>
                <div v-if="typeValidationError" class="invalid-feedback">Wybór typu raportu jest wymagany</div>
              </div>
            </div>
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-secondary" @click="cancel">
              <i class="fas fa-times me-1"></i>Anuluj
            </button>
            <button type="button" class="btn btn-primary" @click="loadDefaultConfig" :disabled="!selectedReportType || loadingConfig">
              <span v-if="loadingConfig"><i class="fas fa-spinner fa-spin me-1"></i>Ładowanie...</span>
              <span v-else><i class="fas fa-arrow-right me-1"></i>Dalej</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Full form (after type selection in create mode or directly in edit mode) -->
      <div v-if="isEditMode || typeSelected">

        <!-- Loading indicator -->
        <div v-if="loadingForm" class="text-center py-5">
          <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p class="mt-2 text-muted">Ładowanie danych formularza...</p>
        </div>

        <div v-else>
          <!-- Tabs navigation -->
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <a class="nav-link" :class="{active: activeTab === 'general'}" href="#" @click.prevent="activeTab = 'general'">
                <i class="fas fa-info-circle me-1"></i>Ogólne
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{active: activeTab === 'conditions', 'text-danger': conditionsHaveErrors}" href="#" @click.prevent="activeTab = 'conditions'">
                <i class="fas fa-filter me-1"></i>Warunki
                <span v-if="visibleConditions.length > 0" class="badge bg-secondary ms-1">{{ visibleConditions.length }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{active: activeTab === 'settings', 'text-danger': settingsHaveErrors}" href="#" @click.prevent="activeTab = 'settings'">
                <i class="fas fa-cog me-1"></i>Ustawienia
                <span v-if="visibleSettings.length > 0" class="badge bg-secondary ms-1">{{ visibleSettings.length }}</span>
              </a>
            </li>
            <li v-if="formData.queries && formData.queries.length > 0" class="nav-item">
              <a class="nav-link" :class="{active: activeTab === 'queries'}" href="#" @click.prevent="activeTab = 'queries'">
                <i class="fas fa-database me-1"></i>Zapytania
                <span class="badge bg-secondary ms-1">{{ formData.queries.length }}</span>
              </a>
            </li>
          </ul>

          <!-- Tab: General -->
          <div v-if="activeTab === 'general'">
            <div class="card shadow-sm mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0 text-primary fw-bold">
                  <i class="fas fa-info-circle me-2"></i>Informacje podstawowe
                </h6>
              </div>
              <div class="card-body">
                <div class="row g-3">

                  <div class="col-md-6">
                    <label class="form-label required">Typ raportu</label>
                    <input
                        type="text"
                        class="form-control"
                        :value="formData.reportType"
                        readonly
                        disabled
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label required">Nazwa raportu</label>
                    <input
                        type="text"
                        :class="['form-control', nameInputClass]"
                        v-model="formData.name"
                        placeholder="Wprowadź nazwę raportu"
                        @blur="validateNameOnBlur"
                    />
                    <div class="invalid-feedback">{{ nameErrorMessage }}</div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Opis</label>
                    <textarea
                        class="form-control"
                        rows="2"
                        v-model="formData.description"
                        placeholder="Opis raportu"
                    ></textarea>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Autor</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.creator"
                        placeholder="Imię i nazwisko autora"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">GUID raportu centralnego</label>
                    <input
                        type="text"
                        class="form-control font-monospace"
                        v-model="formData.reportCentralGuid"
                        placeholder="Opcjonalnie"
                    />
                  </div>

                  <div class="col-md-4">
                    <label class="form-label text-muted small">Wersja konfiguracji</label>
                    <input
                        type="number"
                        class="form-control"
                        v-model.number="formData.configStructureVersion"
                        readonly
                        disabled
                    />
                  </div>

                  <div v-if="formData.reportGuid" class="col-md-8">
                    <label class="form-label text-muted small">GUID raportu</label>
                    <input
                        type="text"
                        class="form-control font-monospace"
                        :value="formData.reportGuid"
                        readonly
                        disabled
                    />
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Conditions -->
          <div v-if="activeTab === 'conditions'">
            <div v-if="visibleConditions.length === 0" class="alert alert-info">
              <i class="fas fa-info-circle me-1"></i>Ten raport nie posiada konfigurowalnych warunków.
            </div>

            <div v-for="(condition, idx) in visibleConditions" :key="condition.conditionReference || idx" class="card shadow-sm mb-3">
              <div class="card-header bg-light py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <h6 class="mb-0 fw-semibold">
                    {{ condition.name }}
                    <span v-if="condition.requireNotEmpty" class="text-danger ms-1" title="Pole wymagane">*</span>
                    <span v-if="condition.isLocal" class="badge bg-info-subtle text-info ms-2 small">Lokalne</span>
                  </h6>
                  <span class="text-muted small font-monospace">{{ condition.conditionReference }}</span>
                </div>
              </div>
              <div class="card-body py-2">

                <!-- Range condition (fromValue / toValue) -->
                <div v-if="isRangeCondition(condition)" class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small text-muted">Od</label>
                    <input
                        type="text"
                        :class="['form-control form-control-sm', condition.requireNotEmpty && !condition.fromValue ? 'is-invalid' : '']"
                        v-model="condition.fromValue"
                        placeholder="Wartość od"
                    />
                    <div v-if="condition.requireNotEmpty && !condition.fromValue" class="invalid-feedback">Pole wymagane</div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small text-muted">Do</label>
                    <input
                        type="text"
                        :class="['form-control form-control-sm', condition.requireNotEmpty && !condition.toValue ? 'is-invalid' : '']"
                        v-model="condition.toValue"
                        placeholder="Wartość do"
                    />
                    <div v-if="condition.requireNotEmpty && !condition.toValue" class="invalid-feedback">Pole wymagane</div>
                  </div>
                </div>

                <!-- Collection condition -->
                <div v-else-if="isCollectionCondition(condition)">
                  <label class="form-label small text-muted">Wartości (jedna per linia lub oddzielone przecinkiem)</label>
                  <textarea
                      :class="['form-control form-control-sm', condition.requireNotEmpty && !condition.collectionValue ? 'is-invalid' : '']"
                      rows="3"
                      v-model="condition.collectionValue"
                      placeholder="Wartości kolekcji"
                  ></textarea>
                  <div v-if="condition.requireNotEmpty && !condition.collectionValue" class="invalid-feedback">Pole wymagane</div>
                </div>

                <!-- Simple value condition -->
                <div v-else>
                  <input
                      :type="getConditionInputType(condition)"
                      :class="['form-control form-control-sm', condition.requireNotEmpty && !condition.value ? 'is-invalid' : '']"
                      v-model="condition.value"
                      :placeholder="condition.name"
                  />
                  <div v-if="condition.requireNotEmpty && !condition.value" class="invalid-feedback">Pole wymagane</div>
                </div>

              </div>
            </div>
          </div>

          <!-- Tab: Settings -->
          <div v-if="activeTab === 'settings'">
            <div v-if="visibleSettings.length === 0" class="alert alert-info">
              <i class="fas fa-info-circle me-1"></i>Ten raport nie posiada konfigurowalnych ustawień.
            </div>

            <div v-for="(setting, idx) in visibleSettings" :key="setting.settingReference || idx" class="card shadow-sm mb-3">
              <div class="card-header bg-light py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <h6 class="mb-0 fw-semibold">
                    {{ setting.name }}
                    <span v-if="setting.requireNotEmpty" class="text-danger ms-1" title="Pole wymagane">*</span>
                    <span v-if="setting.isLocal" class="badge bg-info-subtle text-info ms-2 small">Lokalne</span>
                  </h6>
                  <span class="text-muted small font-monospace">{{ setting.settingReference }}</span>
                </div>
              </div>
              <div class="card-body py-2">

                <!-- Boolean setting -->
                <div v-if="isBooleanSetting(setting)" class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="setting.value === 'true' || setting.value === true"
                      @change="setting.value = $event.target.checked ? 'true' : 'false'"
                  />
                  <label class="form-check-label">{{ setting.name }}</label>
                </div>

                <!-- Number setting -->
                <div v-else-if="isNumberSetting(setting)">
                  <input
                      type="number"
                      :class="['form-control form-control-sm', setting.requireNotEmpty && !setting.value ? 'is-invalid' : '']"
                      v-model="setting.value"
                      :placeholder="setting.name"
                  />
                  <div v-if="setting.requireNotEmpty && !setting.value" class="invalid-feedback">Pole wymagane</div>
                </div>

                <!-- Date setting -->
                <div v-else-if="isDateSetting(setting)">
                  <input
                      :type="isDatetimeSetting(setting) ? 'datetime-local' : 'date'"
                      :class="['form-control form-control-sm', setting.requireNotEmpty && !setting.value ? 'is-invalid' : '']"
                      v-model="setting.value"
                  />
                  <div v-if="setting.requireNotEmpty && !setting.value" class="invalid-feedback">Pole wymagane</div>
                </div>

                <!-- Default text setting -->
                <div v-else>
                  <input
                      type="text"
                      :class="['form-control form-control-sm', setting.requireNotEmpty && !setting.value ? 'is-invalid' : '']"
                      v-model="setting.value"
                      :placeholder="setting.name"
                  />
                  <div v-if="setting.requireNotEmpty && !setting.value" class="invalid-feedback">Pole wymagane</div>
                </div>

              </div>
            </div>
          </div>

          <!-- Tab: Queries -->
          <div v-if="activeTab === 'queries'">
            <div v-if="!formData.queries || formData.queries.length === 0" class="alert alert-info">
              <i class="fas fa-info-circle me-1"></i>Ten raport nie posiada zdefiniowanych zapytań.
            </div>

            <div v-for="(query, idx) in formData.queries" :key="query.queryName || idx" class="card shadow-sm mb-3">
              <div class="card-header bg-light py-2">
                <h6 class="mb-0 fw-semibold font-monospace">
                  <i class="fas fa-code me-2 text-muted"></i>{{ query.queryName }}
                </h6>
              </div>
              <div class="card-body py-2">
                <label class="form-label small text-muted">Treść zapytania</label>
                <textarea
                    class="form-control form-control-sm font-monospace"
                    rows="4"
                    v-model="query.query"
                    placeholder="Brak zapytania"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Validation errors -->
          <div v-if="validationErrors.length > 0" class="alert alert-danger mt-3">
            <h6 class="mb-2"><i class="fas fa-exclamation-triangle me-1"></i>Błędy walidacji:</h6>
            <ul class="mb-0 ps-3">
              <li v-for="(error, idx) in validationErrors" :key="idx">{{ error }}</li>
            </ul>
          </div>

          <!-- Action buttons -->
          <div class="modal-footer-container">
            <div class="modal-footer-bottom">
              <button type="button" class="btn btn-secondary" @click="cancel" :disabled="saving">
                <i class="fas fa-times me-1"></i>Anuluj
              </button>
              <button type="button" class="btn btn-primary" @click="save" :disabled="saving">
                <span v-if="saving && !saveAndGenerate"><i class="fas fa-spinner fa-spin me-1"></i>Zapisywanie...</span>
                <span v-else><i class="fas fa-save me-1"></i>Zapisz</span>
              </button>
              <button type="button" class="btn btn-success" @click="saveAndGenerateReport" :disabled="saving">
                <span v-if="saving && saveAndGenerate"><i class="fas fa-spinner fa-spin me-1"></i>Przetwarzanie...</span>
                <span v-else><i class="fas fa-play me-1"></i>Zapisz i generuj</span>
              </button>
            </div>
          </div>

        </div>
      </div>

    </b-modal>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {BModal} from "bootstrap-vue-next";
import {log} from "@/state/pinia/log";
import {apiRequestsHelper} from "@/state/pinia/apiRequestHelper";
import {useReportsManagerModule} from "@/state/pinia/reportsManagerModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

export default defineComponent({
  name: "ReportsFormModal",
  components: {BModal},
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: () => "Formularz raportu",
    },
    reportGuid: {
      type: String,
      default: () => null,
    },
  },
  emits: ["update:showModal", "onSave", "onClose"],
  data() {
    return {
      showModalInternal: this.showModal,

      // Step control
      isEditMode: false,
      typeSelected: false,
      selectedReportType: "",
      typeValidationError: false,
      loadingConfig: false,
      loadingForm: false,

      // Available report types
      availableReportTypes: [],

      // Form state
      activeTab: "general",
      saving: false,
      saveAndGenerate: false,

      // Validation
      validationErrors: [],
      nameValidationState: null, // null, true, false
      nameErrorMessage: "Nazwa raportu jest wymagana",

      // Form data (GeneratedReportEditorDto)
      formData: {
        reportGuid: null,
        reportType: "",
        name: "",
        description: "",
        creator: "",
        reportCentralGuid: null,
        configStructureVersion: 1,
        settings: [],
        conditions: [],
        queries: [],
      },
    };
  },
  computed: {
    reportsModule() {
      return useReportsManagerModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    visibleConditions() {
      if (!this.formData.conditions) return [];
      return this.formData.conditions.filter((c) => c.isVisible !== false);
    },
    visibleSettings() {
      if (!this.formData.settings) return [];
      return this.formData.settings.filter((s) => s.isVisible !== false);
    },
    conditionsHaveErrors() {
      return this.visibleConditions.some(
          (c) => c.requireNotEmpty && !c.value && !c.fromValue && !c.toValue && !c.collectionValue
      );
    },
    settingsHaveErrors() {
      return this.visibleSettings.some((s) => s.requireNotEmpty && !s.value);
    },
    nameInputClass() {
      if (this.nameValidationState === null) return "";
      return this.nameValidationState ? "is-valid" : "is-invalid";
    },
  },
  methods: {
    // ======================== Init ========================
    async init() {
      if (this.reportGuid) {
        // Edit mode
        this.isEditMode = true;
        this.typeSelected = false;
        await this.loadReportForEdit();
      } else {
        // Create mode - load available report types
        this.isEditMode = false;
        this.typeSelected = false;
        await this.loadAvailableReportTypes();
      }
    },

    async loadAvailableReportTypes() {
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie typów raportów...");
      const response = await this.reportsModule.httpGetAvailableReportTypes();
      if (response && response.status === 200) {
        this.availableReportTypes = response.data.payload || [];
      }
    },

    async loadDefaultConfig() {
      if (!this.selectedReportType) {
        this.typeValidationError = true;
        return;
      }
      this.typeValidationError = false;
      this.loadingConfig = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie domyślnej konfiguracji...");
      try {
        const response = await this.reportsModule.httpGetDefaultReportConfig(this.selectedReportType);
        if (response && response.status === 200) {
          const config = response.data.payload;
          this.formData = Object.assign({}, this.formData, config, {
            reportGuid: null,
            reportType: this.selectedReportType,
          });
          this.typeSelected = true;
          this.activeTab = "general";
        }
      } finally {
        this.loadingConfig = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    async loadReportForEdit() {
      this.loadingForm = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie danych raportu...");
      try {
        const response = await this.reportsModule.httpGetReportForEdit(this.reportGuid);
        if (response && response.status === 200) {
          this.formData = Object.assign({}, response.data.payload);
          this.activeTab = "general";
        }
      } finally {
        this.loadingForm = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    // ======================== Validation ========================
    async validateNameOnBlur() {
      if (!this.formData.name) {
        this.nameValidationState = false;
        this.nameErrorMessage = "Nazwa raportu jest wymagana";
        return;
      }
      const response = await this.reportsModule.httpValidateReportName(
          this.formData.name,
          this.formData.reportGuid
      );
      if (response && response.status === 200) {
        const validationResult = response.data.payload;
        if (validationResult && validationResult.valid === false) {
          this.nameValidationState = false;
          this.nameErrorMessage = (validationResult.errors && validationResult.errors.length > 0)
              ? validationResult.errors[0]
              : "Niepoprawna nazwa raportu";
        } else {
          this.nameValidationState = true;
          this.nameErrorMessage = "";
        }
      }
    },

    validateLocalForm() {
      this.validationErrors = [];

      if (!this.formData.name || this.formData.name.trim() === "") {
        this.validationErrors.push("Nazwa raportu jest wymagana");
        this.nameValidationState = false;
        this.nameErrorMessage = "Nazwa raportu jest wymagana";
        this.activeTab = "general";
        return false;
      }

      // Check required conditions
      for (const condition of this.visibleConditions) {
        if (condition.requireNotEmpty) {
          const hasValue = this.isRangeCondition(condition)
              ? (condition.fromValue || condition.toValue)
              : this.isCollectionCondition(condition)
                  ? condition.collectionValue
                  : condition.value;
          if (!hasValue) {
            this.validationErrors.push("Warunek \"" + condition.name + "\" jest wymagany");
          }
        }
      }

      // Check required settings
      for (const setting of this.visibleSettings) {
        if (setting.requireNotEmpty && !setting.value) {
          this.validationErrors.push("Ustawienie \"" + setting.name + "\" jest wymagane");
        }
      }

      if (this.validationErrors.length > 0) {
        return false;
      }

      return true;
    },

    async validateWithBackend() {
      const response = await this.reportsModule.httpValidateReportConfig(this.formData);
      if (response && response.status === 200) {
        const result = response.data.payload;
        if (result && result.valid === false) {
          this.validationErrors = result.errors || ["Konfiguracja raportu zawiera błędy"];
          return false;
        }
      }
      return true;
    },

    // ======================== Save actions ========================
    async save() {
      if (!this.validateLocalForm()) return;

      this.saving = true;
      this.saveAndGenerate = false;
      this.validationErrors = [];

      const isValid = await this.validateWithBackend();
      if (!isValid) {
        this.saving = false;
        return;
      }

      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Zapisywanie raportu...");

      try {
        if (this.isEditMode && this.formData.reportGuid) {
          const response = await this.reportsModule.httpUpdateReport(this.formData.reportGuid, this.formData);
          if (response && response.status === 200) {
            this.$emit("onSave");
            this.closeModal();
          }
        } else {
          const response = await this.reportsModule.httpCreateReport(this.formData);
          if (response && response.status === 200) {
            this.$emit("onSave");
            this.closeModal();
          }
        }
      } finally {
        this.saving = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    async saveAndGenerateReport() {
      if (!this.validateLocalForm()) return;

      this.saving = true;
      this.saveAndGenerate = true;
      this.validationErrors = [];

      const isValid = await this.validateWithBackend();
      if (!isValid) {
        this.saving = false;
        return;
      }

      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Zapisywanie i generowanie raportu...");

      try {
        if (this.isEditMode && this.formData.reportGuid) {
          // Use save-and-generate endpoint for existing reports
          const response = await this.reportsModule.httpSaveAndGenerateReport(this.formData.reportGuid, this.formData);
          if (response && response.status === 200) {
            this.$emit("onSave");
            this.closeModal();
          }
        } else {
          // For new reports: create first, then generate
          const createResponse = await this.reportsModule.httpCreateReport(this.formData);
          if (createResponse && createResponse.status === 200) {
            const createdGuid = createResponse.data.payload && createResponse.data.payload.reportGuid;
            if (createdGuid) {
              this.lazyloaderModule.setProgressBarMessage("Generowanie raportu...");
              await this.reportsModule.httpGenerateReport(createdGuid);
            }
            this.$emit("onSave");
            this.closeModal();
          }
        }
      } finally {
        this.saving = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    cancel() {
      this.closeModal();
    },
    closeModal() {
      this.showModalInternal = false;
      this.$emit("onClose");
    },

    // ======================== Field type helpers ========================
    isRangeCondition(condition) {
      return 'fromValue' in condition && 'toValue' in condition
          && (condition.fromValue !== null || condition.toValue !== null);
    },
    isCollectionCondition(condition) {
      return 'collectionValue' in condition && condition.collectionValue !== null;
    },
    getConditionInputType(condition) {
      const vt = (condition.valueType || "").toUpperCase();
      if (vt === "DATE") return "date";
      if (vt === "DATETIME") return "datetime-local";
      if (vt === "NUMBER" || vt === "INTEGER" || vt === "DECIMAL") return "number";
      return "text";
    },
    isBooleanSetting(setting) {
      return (setting.type || "").toUpperCase() === "BOOLEAN";
    },
    isNumberSetting(setting) {
      const t = (setting.type || "").toUpperCase();
      return t === "NUMBER" || t === "INTEGER" || t === "DECIMAL";
    },
    isDateSetting(setting) {
      const t = (setting.type || "").toUpperCase();
      return t === "DATE" || t === "DATETIME";
    },
    isDatetimeSetting(setting) {
      return (setting.type || "").toUpperCase() === "DATETIME";
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  created() {
    log.log("ReportsFormModal created, reportGuid:", this.reportGuid);
    this.init();
  },
});
</script>

<style scoped>
.modal-footer-container {
  position: sticky;
  bottom: -19px;
  left: 0;
  width: 100%;
  background: #fff;
  z-index: 60;
  padding: 0;
  border-top: 1px solid #dee2e6;
  margin-top: 16px;
}

.modal-footer-bottom {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 0 4px 0;
}

.required::after {
  content: " *";
  color: #f46a6a;
}
</style>
